import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProject } from '../../hooks/ProjectContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  Alert,
  Chip,
  MenuItem,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

// Stage 9.0
// Full-page work area for any Project card.
// This replaces “expand in place”: Expand/Edit navigates here and takes the whole main area.
// Persistence is localStorage-only for now; next stage will wire to Project Summary + per-card APIs.

function storageKeyFor(projectId, type) {
  return `terrra.project.workspace.card.${projectId}.${type}.v0`;
}

function safeDecodeType(type) {
  try {
    return decodeURIComponent(type || '');
  } catch (e) {
    return type || '';
  }
}

function makeDefaultChecklist() {
  return Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    label: `Checklist item ${i + 1}`,
    done: false,
  }));
}

function makeFeasibilityItems() {
  return [
    // Deliverables
    {
      id: 'feasibility.deliverable.subsides',
      group: 'Deliverables',
      name: 'Project Impact on Subsides - Study cocnolusion',
      itemType: 'Document',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'feasibility.deliverable.removal',
      group: 'Deliverables',
      name: 'Estimation of land removal potenial',
      itemType: 'Document',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'feasibility.deliverable.business',
      group: 'Deliverables',
      name: 'Buissnes Model to estimate IRR and Sensitivity',
      itemType: 'Spreadsheet',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },

    // Tasks
    {
      id: 'feasibility.task.plan_rfq',
      group: 'Tasks',
      name: 'Mesuremnt Plan + RFQ',
      itemType: 'Task',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'feasibility.task.execution',
      group: 'Tasks',
      name: 'Mesuremnt excution',
      itemType: 'Task',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'feasibility.task.soil_report',
      group: 'Tasks',
      name: 'Soil Mesuremnt Report',
      itemType: 'Task',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },

    // Evidence
    {
      id: 'feasibility.evidence.sampling_report',
      group: 'Evidence',
      name: 'Sampling report',
      itemType: 'Spreadsheet',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'feasibility.evidence.sampling_pictures',
      group: 'Evidence',
      name: 'Sampling pictures',
      itemType: 'Document',
      requirement: 'Mandatory',
      status: 'not started',
      attachments: [],
    },
  ];
}

function makeOwnershipItems() {
  return [
    // Evidence
    {
      id: 'ownership.evidence.registry_ownership',
      group: 'Evidence',
      name: 'Land ownership in state registry',
      itemType: 'Document',
      requirement: 'Mandatory',
      alternative: 'Declaration acceptable',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'ownership.evidence.lot_parcel_map',
      group: 'Evidence',
      name: 'Lot and Parcel from state registry shown on map',
      itemType: 'Document',
      requirement: 'Optional',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'ownership.evidence.land_use_per_parcel',
      group: 'Evidence',
      name: 'Land Use allowed for each Lot/Parcel from state registry',
      itemType: 'Document',
      requirement: 'Optional',
      status: 'not started',
      attachments: [],
    },
    {
      id: 'ownership.evidence.signature_rights',
      group: 'Evidence',
      name: 'Signature rights of Agreement signee',
      itemType: 'Document',
      requirement: 'Optional',
      status: 'not started',
      attachments: [],
    },

    // Agreement
    {
      id: 'ownership.agreement.carbon_rights_assignment',
      group: 'Agreement',
      name: 'Agreement of Carbon right assignment to Project Proponent',
      itemType: 'Document',
      requirement: 'Mandatory',
      alternative: 'Declaration acceptable',
      status: 'not started',
      attachments: [],
    },
  ];
}

function defaultStateFor(type) {
  const base = {
    title: type,
    shortDescription: '',
    notes: '',
    checklist: makeDefaultChecklist(),
    updatedAt: null,
  };

  if (type === 'Feasibility') {
    return {
      ...base,
      feasibilityItems: makeFeasibilityItems(),
    };
  }
  if (type === 'Ownership') {
    return {
      ...base,
      ownershipItems: makeOwnershipItems(),
    };
  }

  return base;
}

function mergeWithDefaults(type, existing) {
  const defaults = defaultStateFor(type);
  if (!existing) return defaults;

  // Merge shallow fields.
  const merged = { ...defaults, ...existing };

  // Ensure feasibility items exist and merge by id (preserve saved statuses).
  if (type === 'Feasibility') {
    const byId = new Map((existing.feasibilityItems || []).map((x) => [x.id, x]));
    merged.feasibilityItems = makeFeasibilityItems().map((x) => ({ ...x, ...(byId.get(x.id) || {}) }));
  }
  if (type === 'Ownership') {
    const byId = new Map((existing.ownershipItems || []).map((x) => [x.id, x]));
    merged.ownershipItems = makeOwnershipItems().map((x) => ({ ...x, ...(byId.get(x.id) || {}) }));
  }

  // Ensure checklist exists.
  if (!Array.isArray(merged.checklist) || merged.checklist.length === 0) {
    merged.checklist = makeDefaultChecklist();
  }

  return merged;
}

function loadLocalState(projectId, type) {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(storageKeyFor(projectId, type));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function saveLocalState(projectId, type, state) {
  if (!projectId) return;
  localStorage.setItem(storageKeyFor(projectId, type), JSON.stringify(state));
}

function computeProgressFor(type, state) {
  if (type === 'Feasibility') {
    const items = state?.feasibilityItems || [];
    const total = items.length;
    const done = items.filter((x) => x.status === 'done').length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, pct };
  }
  if (type === 'Ownership') {
    const items = state?.ownershipItems || [];
    const total = items.length;
    const done = items.filter((x) => x.status === 'done').length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, pct };
  }

  const checklist = state?.checklist || [];
  const total = checklist.length;
  const done = checklist.filter((x) => x.done).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, pct };
}

function descriptionFor(type) {
  if (type === 'Feasibility') {
    return 'Collect information on site soil, land-use history, regional and local subsidies, and regulatory frameworks to remove main risks and estimate the constraints, risks, and viability for a go/no-go decision';
  }
  if (type === 'Ownership') {
    return 'Project and jurisdictional proponents must demonstrate that they have the legal right to control and operate project or program activities.';
  }
  // Fallback (other cards can be refined later)
  return '';
}


// Upload project file evidence via project-files API (multipart POST).
async function uploadProjectFileViaApi({ projectId, itemId, title, comment, file }) {
  const fd = new FormData();
  fd.append('projectId', String(projectId || '').trim());
  fd.append('itemId', String(itemId || '').trim());
  fd.append('title', String(title || '').trim());
  fd.append('comment', String(comment || '').trim());
  fd.append('contentType', String(file?.type || 'application/octet-stream'));
  fd.append('originalName', String(file?.name || 'file'));
  fd.append('file', file);

  const res = await fetch('/v1/project-files/upload', {
    method: 'POST',
    body: fd,
  });

  const text = await res.text();
  let json = null;
  try { json = JSON.parse(text); } catch (_) { json = null; }

  if (!res.ok || !json || !json.ok) {
    throw new Error(`Upload failed (${res.status}): ${json?.message || text}`);
  }

  return json;
}


function addAttachmentToItems(items, itemId, attachment) {
  return (items || []).map((x) =>
    x.id === itemId
      ? {
          ...x,
          attachments: [...(x.attachments || []), attachment],
        }
      : x
  );
}

// --- SYNC HELPERS: merge server project-files into local UI state ---

function normalizeServerRowToAttachment(r) {
  return {
    artifactId: r.artifactId,
    name: r.originalName || r.name || 'file',
    title: r.title || r.originalName || r.name || 'file',
    comment: r.comment || '',
    contentType: r.contentType || '',
    uploadedAt: r.uploadedAt || r.uploaded_at || new Date().toISOString(),
    url: r.url || '',
    metaUrl: r.metaUrl || '',
    s3Bucket: r.s3Bucket,
    fileKey: r.fileKey,
    metaKey: r.metaKey,
  };
}

function mergeAttachments(existing, incoming) {
  const out = Array.isArray(existing) ? [...existing] : [];
  const seen = new Set(out.map((a) => String(a?.artifactId || '').trim()).filter(Boolean));

  for (const a of Array.isArray(incoming) ? incoming : []) {
    const id = String(a?.artifactId || '').trim();
    if (id && seen.has(id)) continue;
    out.push(a);
    if (id) seen.add(id);
  }

  // Sort newest first when timestamps exist
  out.sort((a, b) => {
    const ta = new Date(a?.uploadedAt || 0).getTime();
    const tb = new Date(b?.uploadedAt || 0).getTime();
    return tb - ta;
  });

  return out;
}

function mergeServerFilesIntoItems(items, serverRows) {
  const byItem = new Map();
  for (const r of Array.isArray(serverRows) ? serverRows : []) {
    const itemId = String(r?.itemId || '').trim();
    if (!itemId) continue;
    const arr = byItem.get(itemId) || [];
    arr.push(normalizeServerRowToAttachment(r));
    byItem.set(itemId, arr);
  }

  return (items || []).map((x) => {
    const incoming = byItem.get(x.id) || [];
    if (!incoming.length) return x;
    return {
      ...x,
      attachments: mergeAttachments(x.attachments || [], incoming),
    };
  });
}

export default function ProjectWorkItemPage() {
  const navigate = useNavigate();
  const { type: rawType } = useParams();
  const [params] = useSearchParams();
  const { selectedProject } = useProject();

  const type = safeDecodeType(rawType);
  const mode = params.get('mode') || 'expand'; // expand | edit
  const isEdit = mode === 'edit';
  const PROJECT_ID = selectedProject?.code || selectedProject?.id?.toString() || '';

  const initial = useMemo(() => {
    const existing = loadLocalState(PROJECT_ID, type);
    return mergeWithDefaults(type, existing);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, PROJECT_ID]);

  const [state, setState] = useState(initial);
  const [saved, setSaved] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadOk, setUploadOk] = useState(false);

  const [debugLines, setDebugLines] = useState([]); // newest first

  const [pendingUpload, setPendingUpload] = useState(null); // { itemId, file, listKey }
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadComment, setUploadComment] = useState('');

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  function isLikelyImage(name) {
    const s = String(name || '').toLowerCase();
    return (
      s.endsWith('.jpg') ||
      s.endsWith('.jpeg') ||
      s.endsWith('.png') ||
      s.endsWith('.gif') ||
      s.endsWith('.webp') ||
      s.endsWith('.bmp') ||
      s.endsWith('.tif') ||
      s.endsWith('.tiff') ||
      s.endsWith('.heic') ||
      s.endsWith('.heif')
    );
  }

  function isImageAttachment(att, meta) {
    const ct = String(meta?.contentType || att?.contentType || '').toLowerCase();
    if (ct.startsWith('image/')) return true;
    return isLikelyImage(att?.name || att?.title || '');
  }

  async function openPreview(attachment) {
    setPreviewError('');
    setPreviewMeta(null);
    setPreviewAttachment(attachment || null);
    setPreviewOpen(true);

    // Best-effort fetch of metadata (source of truth). If it fails, we still show what we have locally.
    const metaUrl = attachment && attachment.metaUrl ? String(attachment.metaUrl) : '';
    if (!metaUrl) return;

    try {
      setPreviewLoading(true);
      dbg('Preview meta fetch', { metaUrl });
      const res = await fetch(metaUrl);
      const txt = await res.text();
      let json = null;
      try { json = JSON.parse(txt); } catch (_) { json = null; }
      if (!res.ok || !json || !json.ok) {
        throw new Error(`Meta fetch failed (${res.status}): ${json?.message || txt}`);
      }
      setPreviewMeta(json.meta || null);
      dbg('Preview meta response', json);
    } catch (e) {
      setPreviewError(e?.message || 'Failed to load metadata');
      dbg('Preview meta error', { message: e?.message || String(e) });
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    setPreviewOpen(false);
    setPreviewAttachment(null);
    setPreviewMeta(null);
    setPreviewLoading(false);
    setPreviewError('');
  }

  function dbg(msg, obj) {
    const ts = new Date().toISOString();
    const line = obj ? `[${ts}] ${msg}\n${JSON.stringify(obj, null, 2)}` : `[${ts}] ${msg}`;
    setDebugLines((v) => [line, ...v].slice(0, 200));
  }

  useEffect(() => {
    setSaved(false);
    setState(initial);
  }, [initial]);

  const progress = computeProgressFor(type, state);

  // --- SYNC FROM SERVER: workspace card state (notes / item statuses) ---
  const [serverCardSynced, setServerCardSynced] = useState(false);

  useEffect(() => { setServerCardSynced(false); }, [PROJECT_ID, type]);

  useEffect(() => {
    if (serverCardSynced || !PROJECT_ID) { setServerCardSynced(true); return; }
    (async () => {
      try {
        const res = await fetch(
          `/v1/project-workspace/card?projectId=${encodeURIComponent(PROJECT_ID)}&cardType=${encodeURIComponent(type)}`
        );
        const json = await res.json();
        if (json.ok && json.state) {
          const merged = mergeWithDefaults(type, json.state);
          setState(merged);
          saveLocalState(PROJECT_ID, type, { ...merged, updatedAt: json.updatedAt || new Date().toISOString() });
          dbg('Server card state loaded', { projectId: PROJECT_ID, cardType: type, source: json.source });
        } else {
          dbg('Server card state: none', { projectId: PROJECT_ID, cardType: type });
        }
      } catch (e) {
        dbg('Server card load error', { message: e?.message || String(e) });
      } finally {
        setServerCardSynced(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PROJECT_ID, type, serverCardSynced]);

  // --- SYNC FROM SERVER: project-files (attachments) ---
  const [syncedFromServer, setSyncedFromServer] = useState(false);

  // Reset file sync when project changes; wait for card state to load first
  useEffect(() => { setSyncedFromServer(false); }, [PROJECT_ID, type]);

  useEffect(() => {
    // Only sync for cards that manage uploads; wait for workspace card state first.
    if (syncedFromServer) return;
    if (!serverCardSynced) return;
    if (type !== 'Feasibility' && type !== 'Ownership') return;

    (async () => {
      try {
        dbg('Sync: project-files list (start)', { projectId: PROJECT_ID });
        const res = await fetch(`/v1/project-files/list?projectId=${encodeURIComponent(PROJECT_ID)}`);
        const txt = await res.text();
        let json = null;
        try { json = JSON.parse(txt); } catch (_) { json = null; }

        if (!res.ok || !json || !json.ok) {
          throw new Error(`List failed (${res.status}): ${json?.message || txt}`);
        }

        const rows = Array.isArray(json.items) ? json.items : [];
        dbg('Sync: project-files list (ok)', { count: rows.length });

        setState((prev) => {
          const next = { ...prev };

          if (type === 'Feasibility') {
            next.feasibilityItems = mergeServerFilesIntoItems(prev.feasibilityItems || [], rows);
          }
          if (type === 'Ownership') {
            next.ownershipItems = mergeServerFilesIntoItems(prev.ownershipItems || [], rows);
          }

          // Persist merged state so we don’t “forget” after refresh.
          try {
            saveLocalState(PROJECT_ID, type, { ...next, updatedAt: new Date().toISOString() });
          } catch (_) {}

          return next;
        });

        setSyncedFromServer(true);
      } catch (e) {
        dbg('Sync: project-files list (error)', { message: e?.message || String(e) });
        setSyncedFromServer(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, PROJECT_ID, syncedFromServer, serverCardSynced]);

  function handleUploadSelect(itemId, file, listKey) {
    try {
      setUploadError('');
      setUploadOk(false);
      setPendingUpload({ itemId, file, listKey });
      setUploadTitle(file?.name || '');
      setUploadComment('');
      dbg('Selected file for upload', { itemId, listKey, name: file?.name, type: file?.type, size: file?.size });
    } catch (e) {
      setUploadError(e?.message || 'Failed to prepare upload');
    }
  }

  async function handleUploadConfirm() {
    if (!pendingUpload || !pendingUpload.file) return;

    const { itemId, file, listKey } = pendingUpload;
    const title = String(uploadTitle || '').trim() || file.name;
    const comment = String(uploadComment || '').trim();

    try {
      setUploadError('');
      setUploadOk(false);
      setUploadingId(itemId);

      dbg('Upload request (project-files)', { projectId: PROJECT_ID, itemId, filename: file.name, contentType: file.type || 'application/octet-stream', title, comment });

      const out = await uploadProjectFileViaApi({
        projectId: PROJECT_ID,
        itemId,
        title,
        comment,
        file,
      });

      dbg('Upload response (project-files)', out);

      const attachment = {
        name: file.name,
        url: out.publicUrl || '',
        uploadedAt: out.meta?.uploadedAt || new Date().toISOString(),
        title,
        comment,
        metaUrl: out.metaUrl || '',
        s3Bucket: out.s3Bucket,
        fileKey: out.fileKey,
        metaKey: out.metaKey,
        artifactId: out.artifactId,
      };

      setState((v) => ({
        ...v,
        [listKey]: addAttachmentToItems(v[listKey], itemId, attachment),
      }));

      setUploadOk(true);
      setPendingUpload(null);
    } catch (e) {
      setUploadError(e?.message || 'Upload failed');
      dbg('Upload error', { message: e?.message || String(e) });
    } finally {
      setUploadingId(null);
    }
  }

  async function onSave() {
    const out = { ...state, updatedAt: new Date().toISOString() };
    saveLocalState(PROJECT_ID, type, out);
    setState(out);
    setSaved(true);
    if (PROJECT_ID) {
      try {
        await fetch('/v1/project-workspace/card', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: PROJECT_ID, cardType: type, state: out }),
        });
        dbg('Server card save ok', { projectId: PROJECT_ID, cardType: type });
      } catch (e) {
        dbg('Server card save error', { message: e?.message || String(e) });
      }
    }
  }

  if (!selectedProject) {
    return (
      <>
        <Helmet><title>{type} | Project</title></Helmet>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 3 }}>Select a project from the header to continue.</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{type} | Project</title>
      </Helmet>

      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">{type}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {progress.pct}% ({progress.done}/{progress.total})
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate('/project/home')}>
              Close card
            </Button>
          </Stack>
        </Stack>

        {saved && state.updatedAt && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved locally. Updated at {new Date(state.updatedAt).toLocaleString()}.
          </Alert>
        )}
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}
        {uploadOk && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Uploaded.
          </Alert>
        )}

        <GridLike>
          <Card sx={{ gridColumn: '1 / -1' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Summary
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {descriptionFor(type) || 'Description will be defined for this card.'}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Working notes"
                  value={state.notes}
                  onChange={(e) => setState((v) => ({ ...v, notes: e.target.value }))}
                  disabled={!isEdit}
                  multiline
                  minRows={6}
                />
              </Box>
            </CardContent>
          </Card>

          {type === 'Feasibility' && (
            <Card sx={{ gridColumn: '1 / -1' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Feasibility Package
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Track feasibility deliverables, tasks, and evidence required for concept establishment.
                </Typography>

                {['Deliverables', 'Tasks', 'Evidence'].map((group) => (
                  <Box key={group} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.85 }}>
                      {group}
                    </Typography>

                    <Stack spacing={0.75}>
                      {(state.feasibilityItems || [])
                        .filter((x) => x.group === group)
                        .map((item) => (
                          <Stack
                            key={item.id}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {item.name}
                              </Typography>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={item.itemType} variant="outlined" />
                                <Chip
                                  size="small"
                                  label={item.requirement || 'Mandatory'}
                                  color={item.requirement === 'Optional' ? 'default' : 'error'}
                                  variant={item.requirement === 'Optional' ? 'outlined' : 'filled'}
                                />
                                {item.alternative && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {item.alternative}
                                  </Typography>
                                )}
                              </Stack>
                              {(item.attachments || []).length > 0 && (
                                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                                  {(item.attachments || []).slice(-3).map((a) => (
                                    <Stack
                                      key={a.uploadedAt + a.name}
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ flexWrap: 'wrap' }}
                                    >
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        <Link href={a.url || '#'} target="_blank" rel="noreferrer" underline="hover">
                                          {a.title || a.name}
                                        </Link>
                                        {a.comment ? (
                                          <span style={{ marginLeft: 8 }}>
                                            — {a.comment}
                                          </span>
                                        ) : null}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => openPreview(a)}
                                        sx={{ minWidth: 0, px: 1 }}
                                      >
                                        Preview
                                      </Button>
                                    </Stack>
                                  ))}
                                </Stack>
                              )}
                            </Box>

                            <TextField
                              select
                              size="small"
                              label="Status"
                              value={item.status}
                              disabled={!isEdit}
                              onChange={(e) =>
                                setState((v) => ({
                                  ...v,
                                  feasibilityItems: (v.feasibilityItems || []).map((x) =>
                                    x.id === item.id ? { ...x, status: e.target.value } : x
                                  ),
                                }))
                              }
                              sx={{ minWidth: 160 }}
                            >
                              <MenuItem value="not started">not started</MenuItem>
                              <MenuItem value="in progress">in progress</MenuItem>
                              <MenuItem value="done">done</MenuItem>
                            </TextField>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!isEdit || uploadingId === item.id}
                              component="label"
                            >
                              {uploadingId === item.id ? 'Uploading…' : 'Upload'}
                              <input
                                hidden
                                type="file"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) handleUploadSelect(item.id, f, 'feasibilityItems');
                                }}
                              />
                            </Button>
                          </Stack>
                        ))}
                    </Stack>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {type === 'Ownership' && (
            <Card sx={{ gridColumn: '1 / -1' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Ownership Package
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Track land tenure evidence and carbon-rights agreement documents required for feasibility.
                </Typography>

                {['Evidence', 'Agreement'].map((group) => (
                  <Box key={group} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.85 }}>
                      {group}
                    </Typography>

                    <Stack spacing={0.75}>
                      {(state.ownershipItems || [])
                        .filter((x) => x.group === group)
                        .map((item) => (
                          <Stack
                            key={item.id}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {item.name}
                              </Typography>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={item.itemType} variant="outlined" />
                                <Chip
                                  size="small"
                                  label={item.requirement || 'Mandatory'}
                                  color={item.requirement === 'Optional' ? 'default' : 'error'}
                                  variant={item.requirement === 'Optional' ? 'outlined' : 'filled'}
                                />
                                {item.alternative && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {item.alternative}
                                  </Typography>
                                )}
                              </Stack>

                              {(item.attachments || []).length > 0 && (
                                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                                  {(item.attachments || []).slice(-3).map((a) => (
                                    <Stack
                                      key={a.uploadedAt + a.name}
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ flexWrap: 'wrap' }}
                                    >
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        <Link href={a.url || '#'} target="_blank" rel="noreferrer" underline="hover">
                                          {a.title || a.name}
                                        </Link>
                                        {a.comment ? (
                                          <span style={{ marginLeft: 8 }}>
                                            — {a.comment}
                                          </span>
                                        ) : null}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => openPreview(a)}
                                        sx={{ minWidth: 0, px: 1 }}
                                      >
                                        Preview
                                      </Button>
                                    </Stack>
                                  ))}
                                </Stack>
                              )}
                            </Box>

                            <TextField
                              select
                              size="small"
                              label="Status"
                              value={item.status}
                              disabled={!isEdit}
                              onChange={(e) =>
                                setState((v) => ({
                                  ...v,
                                  ownershipItems: (v.ownershipItems || []).map((x) =>
                                    x.id === item.id ? { ...x, status: e.target.value } : x
                                  ),
                                }))
                              }
                              sx={{ minWidth: 160 }}
                            >
                              <MenuItem value="not started">not started</MenuItem>
                              <MenuItem value="in progress">in progress</MenuItem>
                              <MenuItem value="done">done</MenuItem>
                            </TextField>

                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!isEdit || uploadingId === item.id}
                              component="label"
                            >
                              {uploadingId === item.id ? 'Uploading…' : 'Upload'}
                              <input
                                hidden
                                type="file"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) handleUploadSelect(item.id, f, 'ownershipItems');
                                }}
                              />
                            </Button>
                          </Stack>
                        ))}
                    </Stack>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </GridLike>

        <Dialog open={previewOpen} onClose={closePreview} fullWidth maxWidth="md">
          <DialogTitle>Preview</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {(previewMeta && previewMeta.title) || previewAttachment?.title || previewAttachment?.name || 'File'}
              </Typography>

              {((previewMeta && previewMeta.comment) || previewAttachment?.comment) && (
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {(previewMeta && previewMeta.comment) || previewAttachment?.comment}
                </Typography>
              )}

              {previewError && <Alert severity="warning">{previewError}</Alert>}

              {previewLoading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Loading metadata…
                </Typography>
              ) : null}

              {previewAttachment?.url && isImageAttachment(previewAttachment, previewMeta) ? (
                <Box
                  component="img"
                  src={previewAttachment.url}
                  alt={previewAttachment?.title || previewAttachment?.name || 'preview'}
                  onError={() => {
                    setPreviewError('Image preview failed to load. Use “Open file”.');
                    dbg('Preview image error', { url: previewAttachment?.url, name: previewAttachment?.name });
                  }}
                  sx={{
                    width: '100%',
                    maxHeight: 520,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                  }}
                />
              ) : (
                <Stack spacing={1}>
                  {previewAttachment?.url ? (
                    <Box
                      component="iframe"
                      src={previewAttachment.url}
                      title={previewAttachment?.title || previewAttachment?.name || 'document preview'}
                      sx={{
                        width: '100%',
                        height: 520,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.default',
                      }}
                    />
                  ) : null}

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Use “Open file” to view or download this document.
                  </Typography>

                  {previewAttachment?.url ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      File: {previewAttachment?.name || previewAttachment?.title || 'file'}
                    </Typography>
                  ) : null}
                </Stack>
              )}

              {previewMeta?.s3?.bucket && previewMeta?.s3?.fileKey ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  S3: {previewMeta.s3.bucket} / {previewMeta.s3.fileKey}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closePreview}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                try {
                  window.open(previewAttachment?.url || '#', '_blank', 'noopener,noreferrer');
                } catch (_) {}
              }}
              disabled={!previewAttachment?.url}
            >
              Open file
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!pendingUpload} onClose={() => setPendingUpload(null)} fullWidth maxWidth="sm">
          <DialogTitle>Upload evidence</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                File: {pendingUpload?.file?.name}
              </Typography>

              <TextField fullWidth label="Title" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />

              <TextField
                fullWidth
                label="Comment"
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
                multiline
                minRows={3}
              />

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                A metadata.json will be stored alongside the file in S3 (server-side).
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setPendingUpload(null)} disabled={!!uploadingId}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUploadConfirm} disabled={!!uploadingId}>
              {uploadingId ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Debug
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setDebugLines([])}>
                Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(String(debugLines.join('\n\n')));
                  } catch (_) {}
                }}
              >
                Copy
              </Button>
            </Stack>

            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 220,
                overflow: 'auto',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {debugLines.length ? debugLines.join('\n\n') : 'No debug logs yet.'}
            </Box>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={onSave} disabled={!isEdit}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => navigate('/project/home')}>
            Close card
          </Button>
        </Stack>
      </Container>
    </>
  );
}

// Minimal “2-column cards” layout without introducing new layout dependencies.
function GridLike({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
}
